import { clusterDrilldownTarget } from './transaction-drilldown';

// Bound DOM work independently of the number of administrative units in an API response.
export const MAX_TRANSACTION_MARKERS = 160;
export const MIN_TRANSACTION_MARKER_DISTANCE = 80;

export function groupTransactionMarkers(features, project, width, height, sumFields = []) {
    if (!(width > 0 && height > 0)) return [];
    const points = features.filter(f => f?.geometry?.type === 'Point').map(feature => ({
        feature, point: project(feature.geometry.coordinates),
        count: Math.max(1, Number(feature.properties?.count) || 1),
    })).filter(({ point }) => Number.isFinite(point.x) && Number.isFinite(point.y)
        && point.x >= 0 && point.x <= width && point.y >= 0 && point.y <= height)
        .sort((a, b) => b.count - a.count);
    const budget = Math.min(MAX_TRANSACTION_MARKERS, Math.max(1, Math.floor(width * height / (100 * 100))));
    let distance = MIN_TRANSACTION_MARKER_DISTANCE;
    let groups;
    do {
        const cells = new Map();
        groups = [];
        for (const item of points) {
            const x = Math.floor(item.point.x / distance), y = Math.floor(item.point.y / distance);
            let nearest = null, nearestDistance = distance * distance;
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    for (const group of cells.get(`${x + dx}:${y + dy}`) || []) {
                        const squared = (item.point.x - group.point.x) ** 2 + (item.point.y - group.point.y) ** 2;
                        if (squared <= nearestDistance) { nearest = group; nearestDistance = squared; }
                    }
                }
            }
            if (nearest) nearest.items.push(item);
            else {
                const group = { point: item.point, items: [item] };
                groups.push(group);
                const key = `${x}:${y}`;
                if (!cells.has(key)) cells.set(key, []);
                cells.get(key).push(group);
            }
        }
        distance *= 1.4;
    } while (groups.length > budget);

    return groups.map(({ items }) => {
        if (items.length === 1) return items[0].feature;
        // Keep the largest group's real coordinate as the anchor: no edge projection
        // or drifting average that could reintroduce collisions between markers.
        const feature = items[0].feature;
        const properties = { ...feature.properties, aggregated: true, count: 0, cluster_level: 'grid', cluster_code: null, cluster_name: null };
        for (const key of sumFields) properties[key] = 0;
        const bbox = [Infinity, Infinity, -Infinity, -Infinity];
        const averages = { avg_cena: [0, 0], avg_pm2: [0, 0] };
        const prices = { min_cena: [], max_cena: [] };
        for (const item of items) {
            properties.count += item.count;
            for (const key of sumFields) properties[key] += Number(item.feature.properties?.[key]) || 0;
            const coordinates = item.feature.geometry.coordinates;
            const extent = clusterDrilldownTarget(item.feature).bbox || [...coordinates, ...coordinates];
            bbox[0] = Math.min(bbox[0], extent[0]); bbox[1] = Math.min(bbox[1], extent[1]);
            bbox[2] = Math.max(bbox[2], extent[2]); bbox[3] = Math.max(bbox[3], extent[3]);
            for (const key of Object.keys(averages)) {
                const value = item.feature.properties?.[key];
                if (value != null && Number.isFinite(Number(value))) {
                    averages[key][0] += Number(value) * item.count;
                    averages[key][1] += item.count;
                }
            }
            for (const key of Object.keys(prices)) {
                const value = item.feature.properties?.[key];
                if (value != null && Number.isFinite(Number(value))) prices[key].push(Number(value));
            }
        }
        properties.cluster_bbox = bbox.join(',');
        for (const [key, [sum, weight]] of Object.entries(averages)) properties[key] = weight ? sum / weight : null;
        properties.min_cena = prices.min_cena.length ? Math.min(...prices.min_cena) : null;
        properties.max_cena = prices.max_cena.length ? Math.max(...prices.max_cena) : null;
        return { ...feature, properties };
    });
}
